import * as React from "react";
import { Thread } from "@/components/assistant-ui/thread";
import { MyRuntimeProvider } from "@/alphaEditPopup/components/MyRuntimeProvider";
import { Button } from "antd";
import { useAlphaAgentStore } from "@/state/alphaAgentStore";
import { StepIndicator } from "@/components/ui/StepIndicator";
import clsx from "clsx";
import { getSelectedShape } from "@/utils/sharePricePerformance/selectedPlaceHolderLocalStorage";
import { UIFormatTemplateSelection } from "@alphascriptsolutions/client-core";
import { CSSTransition } from "react-transition-group";
import { labels } from "@/utils/alphaAgent/helper";
import { MyRuntimeProviderFeatureDetails } from "./MyRuntimeProviderFeatureDetails";
import FormatAndPlaceholder from "./FormatAndPlaceholder";
import CustomFeatures from "./CustomFeatures";
import { addFieldsToData, transformGlobalState } from "@/utils/alphaAgent/modelDataHelper";
import { resetEditLLM, resetLLM } from "@/utils/alphaAgent/resetLLM";
import { getCandidatePlaceholders } from "@/utils/candidatePlaceholders";
import { clearPositioningLocalStorage } from "@/utils/clearLocalStorage";
import { getFormatAndPlaceholderValues } from "@/utils/alphaAgent/FormatAndPlaceholderUtils";

function Step({ show, children }) {
  const nodeRef = React.useRef<HTMLDivElement | null>(null);
  return (
    <CSSTransition
      in={show} // ← visible or hidden?
      timeout={150}
      classNames="step" // ← base for all generated class-names
      mountOnEnter={true} // keep it in the DOM from the first render
      unmountOnExit={true} // never un-mount (so state survives)
      appear={show}
      nodeRef={nodeRef}
    >
      <div ref={nodeRef} className="step-slot">{children}</div>
    </CSSTransition>
  );
}

const AlphaEditPopup: React.FC = () => {
  const { currentStep, questionsPerStep, setCurrentStep, setDisabledButton, setIsChatCompleted, isChatCompleted } =
    useAlphaAgentStore();
  const disabledButton = useAlphaAgentStore((state) => state.disabledButton);
  const answersByStep = useAlphaAgentStore((state) => state.answersByStep);

  const handleNextClick = React.useCallback(async () => {
    if (currentStep === 3) {
      const rawData = localStorage.getItem("alphaCreateData");
      const globalStateData = JSON.parse(rawData);
      const convertedDataToRequiredForm = transformGlobalState(globalStateData);
      const { model } = useAlphaAgentStore.getState();

      const presentationDesign = model.getByName<UIFormatTemplateSelection>("presentationDesign");
      const selectedPlaceholder = getSelectedShape();
      const { title: candidateTitleShape, subTitle: candidateSubTitleShape } = getCandidatePlaceholders();
      const tradingVolumeBool = localStorage.getItem("tradingVolume") === "true";
      const { title, subTitle, source, notes } = getFormatAndPlaceholderValues();
      const completeData = addFieldsToData({
        chartObject: convertedDataToRequiredForm,
        customFormatId: presentationDesign.value,
        customPlaceholder: selectedPlaceholder,
        titlePlaceholderShape: candidateTitleShape,
        subTitlePlaceholderShape: candidateSubTitleShape,
        metadata: {
          chartTitle: {
            enabled: true,
            value: title,
          },
          chartSubTitle: {
            enabled: true,
            value: subTitle,
          },
          source: {
            enabled: true,
            value: source,
          },
          notes: {
            enabled: true,
            value: notes,
          },
        },
        tradingVolume: tradingVolumeBool,
        chartSubtitleRight: tradingVolumeBool ? true : undefined,
      });
      localStorage.setItem("ModelDataToBackend", JSON.stringify(completeData));

      localStorage.setItem("modelGlobalState", rawData);
      clearPositioningLocalStorage();
      localStorage.setItem("alphaEdit", "true");
      Office.context.ui.messageParent("INSERT_CHART");
      await resetEditLLM();
      setIsChatCompleted(true);
      return;
    }
    setCurrentStep(currentStep + 1);
    setDisabledButton(true);
  }, [currentStep, setCurrentStep, setDisabledButton]);

  if (isChatCompleted) {
    return (
      <div
        style={{
          boxSizing: "border-box",
          padding: "0 50px",
          fontWeight: "bold",
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          gap: "10px",
        }}
      >
        <p className="text-gray-800">
          Thank you. We are working on the Excel model and placeholder content you requested. We estimate this to take 1
          – 2 minutes. Your Taskpane will open when the content is ready.
        </p>
        <Button type="primary" size="large" onClick={() => Office.context.ui.messageParent("CLOSE")}>
          Close
        </Button>
      </div>
    );
  }

  const handleCancelButton = async () => {
    await resetLLM();
    localStorage.removeItem("messages");
    localStorage.removeItem("ModelData");
    localStorage.removeItem("alphaCreateData");
    localStorage.removeItem("PanelsData");
    localStorage.removeItem("currencySelectionSlotValue");
    localStorage.removeItem("AlphaAgentPlaceholder");
    localStorage.removeItem("selectedShapes");
    Office.context.ui.messageParent("CLOSE");
  };

  // @ts-ignore
  // @ts-ignore
  return (
    <div className="flex flex-col justify-between h-full min-h-screen grid-rows-[auto_1fr_auto] gap-x-2 px-4 py-4">
      <img src="assets/AlphaScript_Logo_Dark_cropped.png" alt="" className="w-36 h-auto object-contain mb-2" />
      <div
        className={clsx(
          "w-full mx-auto max-w-[var(--thread-max-width)] py-4 shadow-md px-4 rounded-sm z-10 border border-gray-200 border-b-[2px] border-b-[#ff9336]"
        )}
      >
        <StepIndicator
          currentStep={currentStep}
          answersByStep={answersByStep}
          questionsPerStep={questionsPerStep}
          labels={labels}
        />
      </div>

      <div className={"grid overflow-y-auto h-full relative w-full"}>
        <Step show={currentStep === 0}>
          <MyRuntimeProvider>
            <Thread />
          </MyRuntimeProvider>
        </Step>

        <Step show={currentStep === 1}>
          <CustomFeatures />
        </Step>

        <Step show={currentStep === 2}>
          <MyRuntimeProviderFeatureDetails>
            <Thread />
          </MyRuntimeProviderFeatureDetails>
        </Step>

        <Step show={currentStep === 3}>
          <FormatAndPlaceholder />
        </Step>
      </div>
      <div className="flex justify-between w-full px-2 pt-4 mx-auto max-w-[var(--thread-max-width)]">
        <Button danger onClick={handleCancelButton}>
          Cancel
        </Button>

        <div className="flex gap-2">
          <Button
            type={"primary"}
            disabled={disabledButton && answersByStep[currentStep] !== questionsPerStep[currentStep]}
            className={""}
            onClick={handleNextClick}
          >
            {currentStep === 3 ? "Complete" : "Next"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AlphaEditPopup;
